import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import { admins, baseURL, currentSeason } from "../../Constants";
import { CONTENT_TEAMS } from "../../config/contentTeams";
import { TierListAnswer, TierListItem, TierListRosterPlayer, TierListSaveData, TierListSummary } from "../../models/TierList";
import {
    saveTierList, getTierListById, getAllMyTierLists, getTierListAnswers, getCareerPool, getTeamSeasonRoster
} from "../../services/TierListService";
import { getDivisionStandings, getDivisionsBySeason, getOldestSeason } from "../../services/MatchService";
import { getPlayersByTeamId } from "../../services/PlayerService";
import { rememberLoginReturn } from "../../utils/loginReturn";
import { rehostImage } from "../../utils/leagueImages";
import PlayerImage from "../../components/PlayerImage";
import Loading from "../../components/Loading";
import GamesBackLink from "../../components/GamesBackLink";
import TierListPoolBuilder from "./TierListPoolBuilder";
import "./TierListPage.css";

const TIERLIST_ALLOWED = new Set([
    ...admins.map(String),
    ...CONTENT_TEAMS.flatMap(t => t.hostIds),
]);

const DEFAULT_LABELS = ["S", "A", "B", "C", "D", "F"];
const TIER_COLORS = ["S", "A", "B", "C", "D", "F"];
const MAX_TIERS = 50;
const MAX_CUSTOM_ITEMS = 300;

// A custom ("Byg selv") list has no season or division; it saves as season 0 + this division and
// keeps its unranked cards in tier_data.pool.
const CUSTOM_DIVISION = "custom";

type TierListType = "teams" | "players";

interface DragPayload {
    source: "pool" | string;
    itemId: string;
}

interface BoardInit {
    mode: TierListType;
    season: number;
    division: string;
    pool: TierListItem[];
    tiers?: { [key: string]: TierListItem[] };
    labels?: string[];
    title?: string;
    savedId?: number | null;
    basedOn?: number | null;
}

const formatDivision = (div: string): string => {
    if (div === "all") return "Alle";
    if (div === "ligaen") return "Ligaen";
    if (div === "1div") return "1. Division";
    if (/^\d+div$/.test(div)) {
        return `${div.charAt(0)}. Division`;
    }
    const match = div.match(/^(\d+)div([A-Z])$/i);
    if (match) {
        return `${match[1]}. Division ${match[2].toUpperCase()}`;
    }
    return div;
};

const emptyTiers = (): { [key: string]: TierListItem[] } => {
    const tiers: { [key: string]: TierListItem[] } = {};
    DEFAULT_LABELS.forEach((_, i) => tiers[String(i)] = []);
    return tiers;
};

const boardKey = (season: number, division: string, mode: TierListType) => `${season}|${division}|${mode}`;

const dedupeById = (items: TierListItem[]): TierListItem[] => {
    const seen = new Set<string>();
    return items.filter(item => {
        if (!item.id || seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
    });
};

// Brings cards saved by older versions up to date:
// - team cards used to be keyed by team id alone; a team card is now that team in one season, so
//   they get their list's season and line up with a freshly loaded pool
// - logos saved before the CDN move point at a host that no longer answers
const withTeamSeason = (items: TierListItem[], type: string, season: number): TierListItem[] =>
    items.map(item => {
        const logo = rehostImage(item.logo);
        const upgraded = logo === item.logo ? item : { ...item, logo };
        return type === "teams" && season > 0 && !upgraded.id.includes(":")
            ? { ...upgraded, id: `${upgraded.id}:${season}`, season }
            : upgraded;
    });

// Parse saved data (cap tiers to MAX_TIERS)
const parseSavedData = (raw: any, type: string, season: number): {
    tiers: { [key: string]: TierListItem[] }; labels: string[]; title: string; pool: TierListItem[]
} => {
    if (raw && raw.tiers && raw.labels) {
        const labels = raw.labels.slice(0, MAX_TIERS);
        const tiers: { [key: string]: TierListItem[] } = {};
        labels.forEach((_: string, i: number) => {
            tiers[String(i)] = withTeamSeason(raw.tiers[String(i)] || [], type, season);
        });
        return { tiers, labels, title: raw.title || "", pool: withTeamSeason(raw.pool || [], type, season) };
    }
    const tiers: { [key: string]: TierListItem[] } = {};
    const labels = [...DEFAULT_LABELS];
    DEFAULT_LABELS.forEach((label, i) => {
        tiers[String(i)] = withTeamSeason(raw[label] || [], type, season);
    });
    return { tiers, labels, title: "", pool: [] };
};

// Fetched for the given season rather than read from the dropdown's state, so reopening an old
// "all" list loads that season's divisions and not the ones currently selected.
const getTargetDivisions = async (season: number, selected: string): Promise<string[]> => {
    if (selected !== "all" && !/^\d+div$/.test(selected)) return [selected];
    const divisions = (await getDivisionsBySeason(season)).filter(d => d.toLowerCase() !== "playoff");
    return selected === "all" ? divisions : divisions.filter(d => d.startsWith(selected));
};

// Load pool items for a given season/division/mode. A custom list starts empty.
const loadPoolItems = async (season: number, division: string, currentMode: TierListType): Promise<TierListItem[]> => {
    if (division === CUSTOM_DIVISION) return [];

    const items: TierListItem[] = [];
    const targets = await getTargetDivisions(season, division);

    if (currentMode === "teams") {
        for (const div of targets) {
            const standings = await getDivisionStandings(season, div);
            const allStandings = Object.values(standings).flat() as any[];
            allStandings.forEach((s: any) => {
                const teamId = String(s.team?.teamId || "");
                if (!teamId) return;
                items.push({
                    id: `${teamId}:${season}`,
                    name: s.team?.teamName || "Unknown",
                    logo: s.team?.teamLogo || undefined,
                    season,
                });
            });
        }
        return dedupeById(items);
    }

    const allTeams: { id: string; name: string }[] = [];
    for (const div of targets) {
        const standings = await getDivisionStandings(season, div);
        const allStandings = Object.values(standings).flat() as any[];
        allStandings.forEach((s: any) => {
            const teamId = String(s.team?.teamId || "");
            const teamName = s.team?.teamName || "Unknown";
            if (teamId) allTeams.push({ id: teamId, name: teamName });
        });
    }
    const rosterResults = await Promise.all(
        allTeams.map(team =>
            getPlayersByTeamId(team.id, season)
                .then(players => players.map(p => ({ ...p, _teamName: team.name })))
                .catch(() => [])
        )
    );
    rosterResults.flat().forEach((p: any) => {
        items.push({
            id: p.steamID || "",
            name: p.account?.playerName || p.playerName || "Unknown",
            teamName: p._teamName || p.team?.teamName,
            rating: p.rating21 || p.rating2,
        });
    });
    return dedupeById(items);
};

// Someone ranking your list should not see your order in their pool.
const sortForRanking = (items: TierListItem[]): TierListItem[] =>
    [...items].sort((a, b) => a.name.localeCompare(b.name, "da") || (a.season ?? 0) - (b.season ?? 0));

const TierListPage: React.FC<{ rank?: boolean }> = ({ rank = false }) => {
    const { t } = useTranslation(["pages", "common"]);
    const navigate = useNavigate();
    const { id: viewId } = useParams<{ id: string }>();
    const { user: visitor, token, authLoaded } = useContext(AuthContext);

    const canCreate = !!visitor;
    // /tierlist/:id shows a list read-only; /tierlist/:id/rank hands its cards to the visitor.
    const isRankMode = rank && !!viewId;
    const isViewMode = !!viewId && !rank;

    // Data state
    const [tierData, setTierData] = useState<{ [key: string]: TierListItem[] }>(emptyTiers);
    const [tierLabels, setTierLabels] = useState<string[]>([...DEFAULT_LABELS]);
    const [title, setTitle] = useState("");
    const [pool, setPool] = useState<TierListItem[]>([]);
    const [mode, setMode] = useState<TierListType>("teams");

    // Selection state
    const [selectedSeason, setSelectedSeason] = useState<number>(currentSeason);
    const [selectedDivision, setSelectedDivision] = useState<string>("");
    const [seasons, setSeasons] = useState<number[]>([]);
    const [divisionOptions, setDivisionOptions] = useState<string[]>([]);
    const isCustom = selectedDivision === CUSTOM_DIVISION;

    // UI state
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState("");
    const [savedTierListId, setSavedTierListId] = useState<number | null>(null);
    const [dragOverTarget, setDragOverTarget] = useState<string | null>(null);
    const [draggingItem, setDraggingItem] = useState<string | null>(null);
    const [editingLabel, setEditingLabel] = useState<number | null>(null);
    const labelInputRef = useRef<HTMLInputElement>(null);
    const [builderNotice, setBuilderNotice] = useState("");

    // Ranking someone else's list: the list it came from (saved as based_on)
    const [basedOn, setBasedOn] = useState<number | null>(null);
    const [rankSourceName, setRankSourceName] = useState("");

    // View mode state
    const [viewCreatorName, setViewCreatorName] = useState<string>("");
    const [viewBasedOn, setViewBasedOn] = useState<{ id: number; creatorName: string } | null>(null);
    const [answers, setAnswers] = useState<TierListAnswer[]>([]);

    // My tierlists state
    const [myTierLists, setMyTierLists] = useState<TierListSummary[]>([]);

    // Pool search & sizing
    const [poolSearch, setPoolSearch] = useState("");
    const [poolSize, setPoolSize] = useState<"sm" | "md" | "lg">("sm");

    // Positional drop tracking
    const [dropInsert, setDropInsert] = useState<{ tier: string; index: number } | null>(null);
    const dropInsertRef = useRef<{ tier: string; index: number } | null>(null);

    // Roster popover for team cards
    const [rosterHover, setRosterHover] = useState<{ item: TierListItem; x: number; y: number; above: boolean } | null>(null);
    const [rosters, setRosters] = useState<Record<string, TierListRosterPlayer[] | null>>({});
    const rosterRequested = useRef(new Set<string>());

    // Which season|division|mode the board currently holds, so the selection effect only loads a
    // fresh pool when the user picked something else (not when a saved list was just opened).
    const boardKeyRef = useRef("");

    const startBoard = useCallback((b: BoardInit) => {
        boardKeyRef.current = boardKey(b.season, b.division, b.mode);
        setMode(b.mode);
        setSelectedSeason(b.season);
        setSelectedDivision(b.division);
        setTierData(b.tiers ?? emptyTiers());
        setTierLabels(b.labels ?? [...DEFAULT_LABELS]);
        setTitle(b.title ?? "");
        setPool(b.pool);
        setSavedTierListId(b.savedId ?? null);
        setBasedOn(b.basedOn ?? null);
        setPoolSearch("");
        setSaveMessage("");
        setBuilderNotice("");
    }, []);

    // Load shared tierlist (view mode)
    useEffect(() => {
        if (!isViewMode) return;
        const loadShared = async () => {
            setLoading(true);
            try {
                const resp = await getTierListById(Number(viewId));
                const tl = resp.tierList;
                const raw = typeof tl.tierData === "string" ? JSON.parse(tl.tierData) : tl.tierData;
                const parsed = parseSavedData(raw, tl.tierListType, tl.season);
                setTierData(parsed.tiers);
                setTierLabels(parsed.labels);
                setTitle(parsed.title);
                setPool(parsed.pool);
                setViewCreatorName(resp.creatorName);
                setViewBasedOn(tl.basedOn ? { id: tl.basedOn, creatorName: resp.basedOnCreatorName || "" } : null);
                setMode(tl.tierListType as TierListType);
                setSelectedSeason(tl.season);
                setSelectedDivision(tl.division);
                getTierListAnswers(tl.tierListId).then(setAnswers).catch(() => setAnswers([]));
            } catch {
                setSaveMessage("Failed to load tierlist.");
            } finally {
                setLoading(false);
            }
        };
        loadShared();
    }, [viewId, isViewMode]);

    // Load the list being ranked (rank mode): every card it has, unranked, same tiers and title
    useEffect(() => {
        if (!isRankMode || !canCreate) return;
        const loadTemplate = async () => {
            setLoading(true);
            try {
                const resp = await getTierListById(Number(viewId));
                const tl = resp.tierList;
                const type = tl.tierListType as TierListType;
                const raw = typeof tl.tierData === "string" ? JSON.parse(tl.tierData) : tl.tierData;
                const parsed = parseSavedData(raw, type, tl.season);
                const ranked = Object.values(parsed.tiers).flat();
                const rest = tl.division === CUSTOM_DIVISION
                    ? parsed.pool
                    : await loadPoolItems(tl.season, tl.division, type);
                startBoard({
                    mode: type,
                    season: tl.season,
                    division: tl.division,
                    pool: sortForRanking(dedupeById([...ranked, ...rest])),
                    labels: parsed.labels,
                    title: parsed.title,
                    basedOn: tl.tierListId,
                });
                setRankSourceName(resp.creatorName);
            } catch {
                setSaveMessage("Failed to load tierlist.");
            } finally {
                setLoading(false);
            }
        };
        loadTemplate();
    }, [isRankMode, canCreate, viewId, startBoard]);

    // Load my tierlists on mount
    useEffect(() => {
        if (isViewMode || isRankMode || !canCreate || !token) return;
        const load = async () => {
            try {
                const lists = await getAllMyTierLists(token);
                setMyTierLists(lists);
            } catch (err) {
                console.error("Error loading my tierlists:", err);
            }
        };
        load();
    }, [isViewMode, isRankMode, canCreate, token]);

    // Load seasons
    useEffect(() => {
        if (isViewMode) return;
        const load = async () => {
            try {
                const oldest = await getOldestSeason();
                const range = Array.from(
                    { length: currentSeason - oldest + 1 },
                    (_, i) => oldest + i
                ).sort((a, b) => b - a);
                setSeasons(range);
            } catch (err: any) {
                console.error("Error loading seasons:", err);
            }
        };
        load();
    }, [isViewMode]);

    // Load divisions when season changes
    useEffect(() => {
        if (isViewMode || !selectedSeason) return;
        const load = async () => {
            try {
                let data = await getDivisionsBySeason(selectedSeason);
                data = data.filter(div => div.toLowerCase() !== "playoff");

                const getOrder = (div: string) => {
                    if (div === "all") return -1;
                    if (div === "ligaen") return 0;
                    if (div.startsWith("1div")) return 10;
                    if (div.startsWith("2div")) return 20;
                    if (div.startsWith("3div")) return 30;
                    if (div.startsWith("4div")) return 40;
                    if (div.startsWith("5div")) return 50;
                    return 60;
                };
                const sorted = data.sort((a, b) => getOrder(a) - getOrder(b));

                const divGroups: { [key: string]: string[] } = {};
                sorted.forEach(d => {
                    const m = d.match(/^(\d+div)/);
                    if (m) {
                        const group = m[1];
                        if (!divGroups[group]) divGroups[group] = [];
                        divGroups[group].push(d);
                    }
                });

                const options: string[] = ["all"];
                const added = new Set<string>();
                sorted.forEach(d => {
                    const m = d.match(/^(\d+div)/);
                    if (m) {
                        const group = m[1];
                        if (divGroups[group] && divGroups[group].length > 1 && !added.has(group)) {
                            options.push(group);
                            added.add(group);
                        }
                    }
                    options.push(d);
                });

                setDivisionOptions(options);
            } catch (err: any) {
                console.error("Error loading divisions:", err);
            }
        };
        load();
    }, [selectedSeason, isViewMode]);

    // Load fresh empty tierlist when division/mode changes
    useEffect(() => {
        if (isViewMode || isRankMode || !selectedDivision) return;
        const key = boardKey(selectedSeason, selectedDivision, mode);
        if (boardKeyRef.current === key) return;
        boardKeyRef.current = key;

        const loadItems = async () => {
            setLoading(true);
            try {
                const items = await loadPoolItems(selectedSeason, selectedDivision, mode);
                // A newer selection may have replaced this one while it loaded.
                if (boardKeyRef.current !== key) return;
                startBoard({ mode, season: selectedSeason, division: selectedDivision, pool: items });
            } catch (err: any) {
                console.error("Error loading items:", err);
            } finally {
                setLoading(false);
            }
        };
        loadItems();
    }, [selectedDivision, selectedSeason, mode, isViewMode, isRankMode, startBoard]);

    // Focus label input when editing
    useEffect(() => {
        if (editingLabel !== null && labelInputRef.current) {
            labelInputRef.current.focus();
            labelInputRef.current.select();
        }
    }, [editingLabel]);

    // The roster popover is fixed to where the card was; scrolling would leave it behind.
    useEffect(() => {
        if (!rosterHover) return;
        const hide = () => setRosterHover(null);
        window.addEventListener("scroll", hide, { passive: true });
        return () => window.removeEventListener("scroll", hide);
    }, [rosterHover]);

    // Auto-scroll viewport when dragging near edges
    useEffect(() => {
        if (!draggingItem || isViewMode) return;

        const EDGE_SIZE = 80;
        const MAX_SPEED = 15;

        let animId: number | null = null;
        let scrollSpeed = 0;

        const scroll = () => {
            if (scrollSpeed !== 0) {
                window.scrollBy(0, scrollSpeed);
                animId = requestAnimationFrame(scroll);
            } else {
                animId = null;
            }
        };

        const handleDragOver = (e: DragEvent) => {
            const y = e.clientY;
            const h = window.innerHeight;

            if (y < EDGE_SIZE) {
                scrollSpeed = -MAX_SPEED * (1 - y / EDGE_SIZE);
            } else if (y > h - EDGE_SIZE) {
                scrollSpeed = MAX_SPEED * (1 - (h - y) / EDGE_SIZE);
            } else {
                scrollSpeed = 0;
            }

            if (scrollSpeed !== 0 && animId === null) {
                animId = requestAnimationFrame(scroll);
            } else if (scrollSpeed === 0 && animId !== null) {
                cancelAnimationFrame(animId);
                animId = null;
            }
        };

        window.addEventListener("dragover", handleDragOver);
        return () => {
            window.removeEventListener("dragover", handleDragOver);
            if (animId !== null) cancelAnimationFrame(animId);
        };
    }, [draggingItem, isViewMode]);

    // --- Drag handlers (rewritten for positional drop + itemId-based) ---

    const handleDragStart = useCallback((source: "pool" | string, itemId: string) =>
        (e: React.DragEvent) => {
            const payload: DragPayload = { source, itemId };
            e.dataTransfer.setData("application/json", JSON.stringify(payload));
            e.dataTransfer.effectAllowed = "move";
            setDraggingItem(itemId);
            setRosterHover(null);
        }, []);

    const handleDragEnd = useCallback(() => {
        setDraggingItem(null);
        setDragOverTarget(null);
        setDropInsert(null);
        dropInsertRef.current = null;
    }, []);

    const handleTierDragOver = useCallback((tierKey: string, tierLength: number) => (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDragOverTarget(tierKey);
        dropInsertRef.current = { tier: tierKey, index: tierLength };
        setDropInsert({ tier: tierKey, index: tierLength });
    }, []);

    const handleItemDragOver = useCallback((tierKey: string, itemIndex: number) => (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "move";
        const rect = e.currentTarget.getBoundingClientRect();
        const midX = rect.left + rect.width / 2;
        const insertIdx = e.clientX < midX ? itemIndex : itemIndex + 1;
        dropInsertRef.current = { tier: tierKey, index: insertIdx };
        setDropInsert({ tier: tierKey, index: insertIdx });
        setDragOverTarget(tierKey);
    }, []);

    const handlePoolDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDragOverTarget("pool");
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        const related = e.relatedTarget as Node | null;
        if (!related || !e.currentTarget.contains(related)) {
            setDragOverTarget(null);
            setDropInsert(null);
            dropInsertRef.current = null;
        }
    }, []);

    const handleDrop = useCallback((target: string) => (e: React.DragEvent) => {
        e.preventDefault();
        const currentInsert = dropInsertRef.current;
        setDragOverTarget(null);
        setDraggingItem(null);
        setDropInsert(null);
        dropInsertRef.current = null;

        try {
            const payload: DragPayload = JSON.parse(e.dataTransfer.getData("application/json"));
            const { source, itemId } = payload;
            const insertAt = currentInsert?.tier === target ? currentInsert.index : undefined;

            // Same tier reorder
            if (source === target && source !== "pool") {
                if (insertAt === undefined) return;
                setTierData(prev => {
                    const next = { ...prev };
                    const items = [...(next[source] || [])];
                    const fromIdx = items.findIndex(it => it.id === itemId);
                    if (fromIdx === -1) return prev;
                    const [moved] = items.splice(fromIdx, 1);
                    const adjustedIdx = fromIdx < insertAt ? insertAt - 1 : insertAt;
                    items.splice(adjustedIdx, 0, moved);
                    next[source] = items;
                    return next;
                });
                return;
            }

            // Tier -> Tier (different)
            if (source !== "pool" && target !== "pool") {
                setTierData(prev => {
                    const next = { ...prev };
                    const sourceItems = [...(next[source] || [])];
                    const idx = sourceItems.findIndex(it => it.id === itemId);
                    if (idx === -1) return prev;
                    const [moved] = sourceItems.splice(idx, 1);
                    next[source] = sourceItems;
                    const targetItems = [...(next[target] || [])];
                    const at = insertAt ?? targetItems.length;
                    targetItems.splice(at, 0, moved);
                    next[target] = targetItems;
                    return next;
                });
            }
            // Pool -> Tier
            else if (source === "pool" && target !== "pool") {
                setPool(prev => {
                    const next = [...prev];
                    const idx = next.findIndex(it => it.id === itemId);
                    if (idx === -1) return prev;
                    const [moved] = next.splice(idx, 1);
                    setTierData(td => {
                        const ntd = { ...td };
                        const targetItems = [...(ntd[target] || [])];
                        const at = insertAt ?? targetItems.length;
                        targetItems.splice(at, 0, moved);
                        ntd[target] = targetItems;
                        return ntd;
                    });
                    return next;
                });
            }
            // Tier -> Pool
            else if (source !== "pool" && target === "pool") {
                setTierData(prev => {
                    const next = { ...prev };
                    const sourceItems = [...(next[source] || [])];
                    const idx = sourceItems.findIndex(it => it.id === itemId);
                    if (idx === -1) return prev;
                    const [moved] = sourceItems.splice(idx, 1);
                    next[source] = sourceItems;
                    setPool(p => [...p, moved]);
                    return next;
                });
            }
        } catch (err) {
            console.error("Drop error:", err);
        }
    }, []);

    // Double-click to return to pool
    const handleDoubleClick = useCallback((tierKey: string, itemId: string) => {
        setTierData(prev => {
            const next = { ...prev };
            const items = [...(next[tierKey] || [])];
            const idx = items.findIndex(it => it.id === itemId);
            if (idx === -1) return prev;
            const [removed] = items.splice(idx, 1);
            next[tierKey] = items;
            setPool(p => [...p, removed]);
            return next;
        });
    }, []);

    // Remove tier row
    const handleRemoveTier = (tierIdx: number) => {
        setEditingLabel(null);
        const key = String(tierIdx);
        const removedItems = tierData[key] || [];
        if (removedItems.length > 0) {
            setPool(p => [...p, ...removedItems]);
        }
        setTierData(prev => {
            const next: { [key: string]: TierListItem[] } = {};
            let newIdx = 0;
            for (let i = 0; i < tierLabels.length; i++) {
                if (i === tierIdx) continue;
                next[String(newIdx)] = prev[String(i)] || [];
                newIdx++;
            }
            return next;
        });
        setTierLabels(prev => prev.filter((_, i) => i !== tierIdx));
    };

    // Add tier row
    const handleAddTier = () => {
        if (tierLabels.length >= MAX_TIERS) return;
        const defaults = ["S", "A", "B", "C", "D", "F", "G", "H"];
        const nextLabel = defaults[tierLabels.length] || `T${tierLabels.length + 1}`;
        const newIdx = String(tierLabels.length);
        setTierLabels(prev => [...prev, nextLabel]);
        setTierData(prev => ({ ...prev, [newIdx]: [] }));
    };

    // Label editing
    const handleLabelClick = (tierIdx: number) => {
        if (isViewMode) return;
        setEditingLabel(tierIdx);
    };

    const handleLabelChange = (tierIdx: number, value: string) => {
        setTierLabels(prev => {
            const next = [...prev];
            next[tierIdx] = value;
            return next;
        });
    };

    const handleLabelBlur = () => setEditingLabel(null);

    const handleLabelKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") setEditingLabel(null);
    };

    // --- Custom pools ---

    const existingIds = useMemo(() => {
        const ids = new Set(pool.map(item => item.id));
        Object.values(tierData).forEach(items => items.forEach(item => ids.add(item.id)));
        return ids;
    }, [pool, tierData]);

    const addToPool = (items: TierListItem[]): number => {
        const seen = new Set(existingIds);
        const room = MAX_CUSTOM_ITEMS - seen.size;
        const fresh: TierListItem[] = [];
        for (const item of items) {
            if (fresh.length >= room) break;
            if (seen.has(item.id)) continue;
            seen.add(item.id);
            fresh.push(item);
        }
        if (fresh.length > 0) setPool(prev => [...prev, ...fresh]);
        return fresh.length;
    };

    const removeFromPool = (itemId: string) => {
        setPool(prev => prev.filter(item => item.id !== itemId));
    };

    const startCustom = () => {
        startBoard({ mode, season: 0, division: CUSTOM_DIVISION, pool: [] });
    };

    // One click from the start screen: your old teams, or everyone you have played with.
    const startCareerList = async (type: TierListType) => {
        if (!visitor) return;
        setLoading(true);
        try {
            const items = await getCareerPool(visitor.steamID, type);
            startBoard({
                mode: type,
                season: 0,
                division: CUSTOM_DIVISION,
                pool: items.slice(0, MAX_CUSTOM_ITEMS),
                title: type === "teams" ? t("pages:tierlist.myOldTeams") : t("pages:tierlist.myTeammates"),
            });
            if (items.length === 0) setBuilderNotice(t("pages:tierlist.noCareer", { name: visitor.playerName }));
        } catch (err) {
            console.error("Error loading career pool:", err);
        } finally {
            setLoading(false);
        }
    };

    const backToStart = () => {
        boardKeyRef.current = "";
        setBasedOn(null);
        setSelectedSeason(currentSeason);
        setSelectedDivision("");
    };

    // --- Roster popover ---

    const showRoster = (item: TierListItem, el: HTMLElement) => {
        if (!item.season || draggingItem) return;
        const rect = el.getBoundingClientRect();
        const above = rect.bottom + 240 > window.innerHeight;
        const x = Math.min(Math.max(rect.left + rect.width / 2, 120), window.innerWidth - 120);
        setRosterHover({ item, x, y: above ? rect.top - 6 : rect.bottom + 6, above });

        if (rosterRequested.current.has(item.id)) return;
        rosterRequested.current.add(item.id);
        setRosters(prev => ({ ...prev, [item.id]: null }));
        getTeamSeasonRoster(item.id.split(":")[0], item.season)
            .then(players => setRosters(prev => ({ ...prev, [item.id]: players })))
            .catch(() => setRosters(prev => ({ ...prev, [item.id]: [] })));
    };

    // Touch screens have no hover, so a tap opens and closes it instead.
    const noHover = typeof window !== "undefined" && window.matchMedia?.("(hover: none)").matches;

    // Save
    const handleSave = async () => {
        if (!token || !canCreate) return;
        setSaving(true);
        setSaveMessage("");
        try {
            const saveData: TierListSaveData = {
                title,
                labels: tierLabels,
                tiers: tierData,
                ...(isCustom ? { pool } : {}),
            };
            const id = await saveTierList(
                selectedSeason, selectedDivision, mode, saveData, token,
                savedTierListId ?? undefined,
                basedOn ?? undefined
            );
            setSavedTierListId(id);
            setSaveMessage(t("pages:tierlist.saved"));
            setTimeout(() => setSaveMessage(""), 3000);

            // Refresh my tierlists panel
            const lists = await getAllMyTierLists(token);
            setMyTierLists(lists);
        } catch (err: any) {
            setSaveMessage("Error saving tierlist.");
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const copyToClipboard = (path: string, message: string) => {
        navigator.clipboard.writeText(`${window.location.origin}${path}`);
        setSaveMessage(message);
        setTimeout(() => setSaveMessage(""), 2000);
    };

    // Copy share link
    const handleCopyLink = () => {
        if (!savedTierListId) return;
        copyToClipboard(`/tierlist/${savedTierListId}`, t("pages:tierlist.copied"));
    };

    const handleCopyRankLink = (id: number) => {
        copyToClipboard(`/tierlist/${id}/rank`, t("pages:tierlist.rankLinkCopied"));
    };

    const loginAndReturn = () => {
        rememberLoginReturn(window.location.pathname);
        window.location.href = `${baseURL}/Auth/steam`;
    };

    // Filtered pool
    const poolQuery = poolSearch.toLowerCase();
    const filteredPool = poolSearch
        ? pool.filter(item =>
            item.name.toLowerCase().includes(poolQuery) ||
            (item.teamName && item.teamName.toLowerCase().includes(poolQuery)) ||
            (item.season != null && `s${item.season}` === poolQuery)
          )
        : pool;

    // The pool of a list ranked from a link is fixed; only your own custom list can be edited.
    const canEditPool = isCustom && !isViewMode && !isRankMode && basedOn === null;
    const scopeLabel = (season: number, division: string) =>
        division === CUSTOM_DIVISION
            ? t("pages:tierlist.sourceCustom")
            : `Season ${season} · ${formatDivision(division)}`;

    // Render item card
    const renderItem = (item: TierListItem, source: string, index: number, inTier: boolean = false) => {
        const isDraggingThis = draggingItem === item.id;
        const isPlayer = mode === "players";
        const tierItems = inTier ? (tierData[source] || []) : [];
        const isDropBefore = inTier && dropInsert?.tier === source && dropInsert?.index === index;
        const isDropAfter = inTier && dropInsert?.tier === source && dropInsert?.index === index + 1
            && index === tierItems.length - 1;
        const hasRoster = !isPlayer && !!item.season;

        return (
            <div
                key={`${source}-${item.id}`}
                className={`tier-item ${isDraggingThis ? "dragging" : ""} ${isViewMode ? "view-only" : ""} ${isDropBefore ? "drop-before" : ""} ${isDropAfter ? "drop-after" : ""}`}
                draggable={!isViewMode}
                onDragStart={!isViewMode ? handleDragStart(source, item.id) : undefined}
                onDragEnd={!isViewMode ? handleDragEnd : undefined}
                onDragOver={!isViewMode && inTier ? handleItemDragOver(source, index) : undefined}
                onDoubleClick={!isViewMode && source !== "pool" ? () => handleDoubleClick(source, item.id) : undefined}
                onMouseEnter={hasRoster && !noHover ? e => showRoster(item, e.currentTarget) : undefined}
                onMouseLeave={hasRoster && !noHover ? () => setRosterHover(null) : undefined}
                onClick={hasRoster && noHover ? e => {
                    if (rosterHover?.item.id === item.id) setRosterHover(null);
                    else showRoster(item, e.currentTarget);
                } : undefined}
                title={hasRoster ? undefined : isViewMode ? item.name : `${item.name} (double-click to remove)`}
            >
                {canEditPool && source === "pool" && (
                    <button
                        className="tier-item-remove"
                        draggable={false}
                        onClick={e => { e.stopPropagation(); removeFromPool(item.id); }}
                        title={t("pages:tierlist.removeFromPool")}
                    >
                        &times;
                    </button>
                )}
                {isPlayer ? (
                    <div className="tier-item-img-wrapper">
                        <PlayerImage id={item.id} width={48} />
                    </div>
                ) : item.logo ? (
                    <img
                        src={item.logo}
                        alt={item.name}
                        className="tier-item-img"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                ) : null}
                <span className="tier-item-name">{item.name}</span>
                {isCustom && item.season != null && (
                    <span className="tier-item-season">S{item.season}</span>
                )}
                {item.teamName && (
                    <span className="tier-item-team">{item.teamName}</span>
                )}
                {item.rating !== undefined && (
                    <span className="tier-item-rating">{item.rating.toFixed(2)}</span>
                )}
            </div>
        );
    };

    // Login gate
    if (!isViewMode && !canCreate) {
        if (!authLoaded) {
            return (
                <div className="tierlist-container">
                    <Loading />
                </div>
            );
        }
        return (
            <div className="tierlist-container">
                <div className="tierlist-admin-msg">
                    <h2>{t("pages:tierlist.title")}</h2>
                    <p>{isRankMode ? t("pages:tierlist.loginToRank") : t("pages:tierlist.adminOnly")}</p>
                    <button className="tierlist-save-btn" onClick={loginAndReturn}>
                        {t("pages:tierlist.loginWithSteam")}
                    </button>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="tierlist-container">
                <Loading />
            </div>
        );
    }

    const roster = rosterHover ? rosters[rosterHover.item.id] : undefined;

    return (
        <div className="tierlist-container">
            <GamesBackLink />

            <div className="tierlist-header">
                <h1>{t("pages:tierlist.title")}</h1>
                {!isViewMode && !isRankMode && <p className="tierlist-subtitle">{t("pages:tierlist.subtitle")}</p>}
            </div>

            {/* View mode banner */}
            {isViewMode && (
                <div className="tierlist-view-banner">
                    {title && <div className="tierlist-view-title">{title}</div>}
                    {t("pages:tierlist.viewMode")} <strong>{viewCreatorName}</strong>
                    <div className="tierlist-meta">
                        {selectedDivision === CUSTOM_DIVISION ? (
                            <span>{t("pages:tierlist.sourceCustom")}</span>
                        ) : (
                            <>
                                <span>Season {selectedSeason}</span>
                                <span>{formatDivision(selectedDivision)}</span>
                            </>
                        )}
                        <span>{mode === "teams" ? t("pages:tierlist.teams") : t("pages:tierlist.players")}</span>
                    </div>
                    {viewBasedOn && (
                        <div className="tierlist-meta">
                            <Link to={`/tierlist/${viewBasedOn.id}`} className="tierlist-link">
                                {t("pages:tierlist.answerTo")} {viewBasedOn.creatorName}
                            </Link>
                        </div>
                    )}
                    <div className="tierlist-view-actions">
                        <button className="tierlist-save-btn" onClick={() => navigate(`/tierlist/${viewId}/rank`)}>
                            {t("pages:tierlist.rankYourself")}
                        </button>
                        <button className="tierlist-new-btn" onClick={() => handleCopyRankLink(Number(viewId))}>
                            {t("pages:tierlist.copyRankLink")}
                        </button>
                        {saveMessage && <span className="tierlist-save-msg">{saveMessage}</span>}
                    </div>
                </div>
            )}

            {/* Rank mode banner */}
            {isRankMode && basedOn !== null && (
                <div className="tierlist-view-banner">
                    {t("pages:tierlist.rankingFrom")} <strong>{rankSourceName}</strong>
                    <div className="tierlist-meta">
                        <Link to={`/tierlist/${basedOn}`} className="tierlist-link">
                            {t("pages:tierlist.seeOriginal")}
                        </Link>
                    </div>
                </div>
            )}

            {/* Controls (create mode only) */}
            {!isViewMode && !isRankMode && (
                <div className="tierlist-controls">
                    <div className="tierlist-mode-toggle">
                        <button
                            className={`tierlist-mode-btn ${!isCustom ? "active" : ""}`}
                            onClick={() => { if (isCustom) backToStart(); }}
                        >
                            {t("pages:tierlist.sourceSeason")}
                        </button>
                        <button
                            className={`tierlist-mode-btn ${isCustom ? "active" : ""}`}
                            onClick={() => { if (!isCustom) startCustom(); }}
                        >
                            {t("pages:tierlist.sourceCustom")}
                        </button>
                    </div>

                    <div className="tierlist-mode-toggle">
                        <button
                            className={`tierlist-mode-btn ${mode === "teams" ? "active" : ""}`}
                            onClick={() => setMode("teams")}
                        >
                            {t("pages:tierlist.teams")}
                        </button>
                        <button
                            className={`tierlist-mode-btn ${mode === "players" ? "active" : ""}`}
                            onClick={() => setMode("players")}
                        >
                            {t("pages:tierlist.players")}
                        </button>
                    </div>

                    {!isCustom && (
                        <>
                            <div className="tierlist-dropdown dropdown">
                                <button
                                    className="tierlist-dropdown-btn dropdown-toggle"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                >
                                    Season {selectedSeason}
                                </button>
                                <ul className="dropdown-menu" style={{
                                    background: "rgba(26, 26, 26, 0.95)",
                                    border: "1px solid rgba(255, 102, 0, 0.2)",
                                }}>
                                    {seasons.map(s => (
                                        <li key={s}>
                                            <button
                                                className="dropdown-item"
                                                style={{ color: "rgba(255,255,255,0.9)" }}
                                                onClick={() => { setSelectedSeason(s); setSelectedDivision(""); }}
                                                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,102,0,0.1)"; }}
                                                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                                            >
                                                Season {s}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="tierlist-dropdown dropdown">
                                <button
                                    className="tierlist-dropdown-btn dropdown-toggle"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                >
                                    {selectedDivision ? formatDivision(selectedDivision) : t("pages:tierlist.selectDivision")}
                                </button>
                                <ul className="dropdown-menu" style={{
                                    background: "rgba(26, 26, 26, 0.95)",
                                    border: "1px solid rgba(255, 102, 0, 0.2)",
                                    maxHeight: "400px",
                                    overflowY: "auto",
                                }}>
                                    {divisionOptions.map(d => {
                                        const isGroup = d === "all" || /^\d+div$/.test(d);
                                        return (
                                            <li key={d}>
                                                <button
                                                    className="dropdown-item"
                                                    style={{
                                                        color: "rgba(255,255,255,0.9)",
                                                        fontWeight: isGroup ? 600 : 400,
                                                        paddingLeft: isGroup ? "16px" : "28px",
                                                    }}
                                                    onClick={() => setSelectedDivision(d)}
                                                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,102,0,0.1)"; }}
                                                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                                                >
                                                    {formatDivision(d)}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Title input */}
            {!isViewMode && selectedDivision && (
                <div className="tierlist-title-row">
                    <input
                        type="text"
                        className="tierlist-title-input"
                        placeholder={t("pages:tierlist.titlePlaceholder")}
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        maxLength={100}
                    />
                </div>
            )}

            {/* Save / Share actions */}
            {!isViewMode && canCreate && selectedDivision && (
                <div className="tierlist-actions">
                    <button
                        className="tierlist-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? t("pages:tierlist.saving") : t("pages:tierlist.save")}
                    </button>
                    {savedTierListId && (
                        <>
                            {!isRankMode && (
                                <button
                                    className="tierlist-new-btn"
                                    onClick={() => {
                                        const allItems: TierListItem[] = [];
                                        Object.values(tierData).forEach(items => allItems.push(...items));
                                        setTierData(emptyTiers());
                                        setTierLabels([...DEFAULT_LABELS]);
                                        setTitle("");
                                        setPool(prev => [...prev, ...allItems]);
                                        setSavedTierListId(null);
                                        setSaveMessage("");
                                    }}
                                >
                                    {t("pages:tierlist.newTierlist")}
                                </button>
                            )}
                            <div className="tierlist-share-link">
                                <span>/tierlist/{savedTierListId}</span>
                                <button onClick={handleCopyLink}>{t("pages:tierlist.shareLink")}</button>
                                {basedOn === null && (
                                    <button onClick={() => handleCopyRankLink(savedTierListId)}>
                                        {t("pages:tierlist.copyRankLink")}
                                    </button>
                                )}
                            </div>
                            {basedOn !== null && (
                                <Link to={`/tierlist/${basedOn}`} className="tierlist-link">
                                    {t("pages:tierlist.seeAllAnswers")}
                                </Link>
                            )}
                        </>
                    )}
                    {saveMessage && <span className="tierlist-save-msg">{saveMessage}</span>}
                </div>
            )}

            {/* Tier Board */}
            {(selectedDivision || isViewMode) && (
                <>
                    <div className="tier-board">
                        {tierLabels.map((label, tierIdx) => {
                            const tierKey = String(tierIdx);
                            const items = tierData[tierKey] || [];
                            return (
                                <div key={tierIdx} className="tier-row">
                                    <div
                                        className={`tier-label tier-label-${TIER_COLORS[tierIdx % TIER_COLORS.length] || "F"}`}
                                        onClick={() => handleLabelClick(tierIdx)}
                                        title={isViewMode ? label : "Click to edit"}
                                    >
                                        {editingLabel === tierIdx ? (
                                            <input
                                                ref={labelInputRef}
                                                className="tier-label-input"
                                                value={label}
                                                onChange={e => handleLabelChange(tierIdx, e.target.value)}
                                                onBlur={handleLabelBlur}
                                                onKeyDown={handleLabelKeyDown}
                                                maxLength={20}
                                            />
                                        ) : (
                                            <span className="tier-label-text">{label}</span>
                                        )}
                                        {!isViewMode && tierLabels.length > 1 && (
                                            <button
                                                className="tier-remove-btn"
                                                onClick={(e) => { e.stopPropagation(); handleRemoveTier(tierIdx); }}
                                                title="Remove tier"
                                            >
                                                &times;
                                            </button>
                                        )}
                                    </div>
                                    <div
                                        className={`tier-items ${dragOverTarget === tierKey ? "drag-over" : ""}`}
                                        onDragOver={!isViewMode ? handleTierDragOver(tierKey, items.length) : undefined}
                                        onDragLeave={!isViewMode ? handleDragLeave : undefined}
                                        onDrop={!isViewMode ? handleDrop(tierKey) : undefined}
                                    >
                                        {items.map((item, i) => renderItem(item, tierKey, i, true))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {!isViewMode && tierLabels.length < MAX_TIERS && (
                        <button className="tier-add-btn" onClick={handleAddTier}>
                            + Add Tier
                        </button>
                    )}
                </>
            )}

            {/* Custom pool builder */}
            {canEditPool && (
                <TierListPoolBuilder
                    mode={mode}
                    existingIds={existingIds}
                    onAdd={addToPool}
                    me={visitor ? { steamId: visitor.steamID, name: visitor.playerName } : null}
                    notice={builderNotice}
                    setNotice={setBuilderNotice}
                />
            )}

            {/* Item Pool */}
            {!isViewMode && selectedDivision && (
                <div className="item-pool">
                    <div className="item-pool-header-row">
                        <span className="item-pool-header">{t("pages:tierlist.notRanked")} ({pool.length})</span>
                        <div className="pool-controls">
                            <input
                                type="text"
                                className="pool-search-input"
                                placeholder={mode === "players" ? "Search player or team..." : "Search..."}
                                value={poolSearch}
                                onChange={e => setPoolSearch(e.target.value)}
                            />
                            <div className="pool-size-toggle">
                                <button
                                    className={`pool-size-btn ${poolSize === "sm" ? "active" : ""}`}
                                    onClick={() => setPoolSize("sm")}
                                    title="Small"
                                >S</button>
                                <button
                                    className={`pool-size-btn ${poolSize === "md" ? "active" : ""}`}
                                    onClick={() => setPoolSize("md")}
                                    title="Medium"
                                >M</button>
                                <button
                                    className={`pool-size-btn ${poolSize === "lg" ? "active" : ""}`}
                                    onClick={() => setPoolSize("lg")}
                                    title="Large"
                                >L</button>
                            </div>
                        </div>
                    </div>
                    <div
                        className={`item-pool-items pool-size-${poolSize} ${dragOverTarget === "pool" ? "drag-over" : ""}`}
                        onDragOver={handlePoolDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop("pool")}
                    >
                        {filteredPool.length > 0 ? (
                            filteredPool.map((item, i) => renderItem(item, "pool", i))
                        ) : pool.length > 0 ? (
                            <div className="pool-no-results">No matches for "{poolSearch}"</div>
                        ) : isCustom && existingIds.size === 0 ? (
                            <div className="pool-no-results">{t("pages:tierlist.emptyCustomPool")}</div>
                        ) : (
                            <div className="pool-no-results">All items ranked!</div>
                        )}
                    </div>
                </div>
            )}

            {/* Unranked cards of a shared list, read-only */}
            {isViewMode && pool.length > 0 && (
                <div className="item-pool">
                    <div className="item-pool-header-row">
                        <span className="item-pool-header">{t("pages:tierlist.notRanked")} ({pool.length})</span>
                    </div>
                    <div className="item-pool-items">
                        {pool.map((item, i) => renderItem(item, "pool", i))}
                    </div>
                </div>
            )}

            {/* Everyone who ranked this list from its rank link */}
            {isViewMode && answers.length > 0 && (
                <div className="tierlist-answers">
                    <div className="my-tierlists-title">{t("pages:tierlist.answeredBy")} ({answers.length})</div>
                    <div className="tierlist-answers-list">
                        {answers.map(a => (
                            <Link key={a.tierListId} to={`/tierlist/${a.tierListId}`} className="tierlist-answer-chip">
                                {a.creatorName}
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Getting started / My Tierlists */}
            {!isViewMode && !isRankMode && !selectedDivision && (
                <>
                    {/* Quick start */}
                    <div className="tierlist-quickstart">
                        <button className="tierlist-quick-card" onClick={() => startCareerList("teams")}>
                            <span className="tierlist-quick-title">{t("pages:tierlist.myOldTeams")}</span>
                            <span className="tierlist-quick-desc">{t("pages:tierlist.myOldTeamsDesc")}</span>
                        </button>
                        <button className="tierlist-quick-card" onClick={() => startCareerList("players")}>
                            <span className="tierlist-quick-title">{t("pages:tierlist.myTeammates")}</span>
                            <span className="tierlist-quick-desc">{t("pages:tierlist.myTeammatesDesc")}</span>
                        </button>
                        <button className="tierlist-quick-card" onClick={startCustom}>
                            <span className="tierlist-quick-title">{t("pages:tierlist.sourceCustom")}</span>
                            <span className="tierlist-quick-desc">{t("pages:tierlist.buildYourOwnDesc")}</span>
                        </button>
                    </div>

                    {/* Getting started prompt */}
                    <div className="tierlist-getting-started">
                        <p>{t("pages:tierlist.gettingStarted")}</p>
                    </div>

                    {/* My tierlists */}
                    {myTierLists.length > 0 && (
                        <div className="my-tierlists">
                            <div className="my-tierlists-title">{t("pages:tierlist.myTierlists")}</div>
                            <div className="my-tierlists-grid">
                                {myTierLists.map(tl => (
                                    <div
                                        key={tl.tierListId}
                                        className="my-tierlist-card"
                                        onClick={async () => {
                                            setLoading(true);
                                            try {
                                                const resp = await getTierListById(tl.tierListId);
                                                const type = tl.tierListType as TierListType;
                                                const raw = typeof resp.tierList.tierData === "string"
                                                    ? JSON.parse(resp.tierList.tierData as string)
                                                    : resp.tierList.tierData;
                                                const parsed = parseSavedData(raw, type, tl.season);

                                                const items = tl.division === CUSTOM_DIVISION
                                                    ? parsed.pool
                                                    : await loadPoolItems(tl.season, tl.division, type);
                                                const tieredIds = new Set<string>();
                                                Object.values(parsed.tiers).forEach(tierItems => {
                                                    tierItems.forEach(item => tieredIds.add(item.id));
                                                });

                                                startBoard({
                                                    mode: type,
                                                    season: tl.season,
                                                    division: tl.division,
                                                    tiers: parsed.tiers,
                                                    labels: parsed.labels,
                                                    title: parsed.title,
                                                    pool: items.filter(item => !tieredIds.has(item.id)),
                                                    savedId: tl.tierListId,
                                                    basedOn: resp.tierList.basedOn ?? null,
                                                });
                                            } catch (err) {
                                                console.error("Error loading tierlist:", err);
                                            } finally {
                                                setLoading(false);
                                            }
                                        }}
                                    >
                                        {tl.title && <div className="my-tierlist-title-text">{tl.title}</div>}
                                        <div className="my-tierlist-type">
                                            {tl.tierListType === "teams" ? t("pages:tierlist.teams") : t("pages:tierlist.players")}
                                        </div>
                                        <div className="my-tierlist-info">
                                            {scopeLabel(tl.season, tl.division)}
                                        </div>
                                        <div className="my-tierlist-date">
                                            {new Date(tl.updatedAt).toLocaleDateString("da-DK")}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Roster of a team card's season */}
            {rosterHover && !draggingItem && (
                <div
                    className={`tier-roster-popover ${rosterHover.above ? "above" : ""}`}
                    style={{ left: rosterHover.x, top: rosterHover.y }}
                >
                    <div className="tier-roster-title">
                        {rosterHover.item.name} · S{rosterHover.item.season}
                    </div>
                    {roster === undefined || roster === null ? (
                        <div className="tier-roster-empty">{t("pages:tierlist.rosterLoading")}</div>
                    ) : roster.length === 0 ? (
                        <div className="tier-roster-empty">{t("pages:tierlist.rosterEmpty")}</div>
                    ) : (
                        roster.map(p => (
                            <div key={p.steamId} className="tier-roster-row">
                                <span>{p.name}</span>
                                <span className="tier-roster-games">{t("pages:tierlist.maps", { count: p.games })}</span>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default TierListPage;
